// client/src/components/admin/cps/CPSSettlementReservationList.js
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

export default function CPSSettlementReservationList() {
  const [filter, setFilter] = useState('pending'); // pending | approved | rejected
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // 예약 목록 조회
  const fetchReservations = async (status = 'pending') => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/cps-settlements/reservations?status=${status}`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setReservations(data.reservations || []);
      } else {
        alert('목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('예약 목록 조회 오류:', error);
      alert('목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations(filter);
  }, [filter]);

  // 상세 보기
  const handleShowDetail = (reservation) => {
    setSelectedReservation(reservation);
    setShowDetailModal(true);
  };

  // 승인 처리
  const handleApprove = async (reservationId) => {
    if (!window.confirm('이 예약을 승인하시겠습니까?\n승인 시 인플루언서에게 즉시 포인트가 지급됩니다.')) {
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/cps-settlements/reservations/${reservationId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        alert('승인이 완료되었습니다.');
        fetchReservations(filter);
      } else {
        alert(data.message || '승인에 실패했습니다.');
      }
    } catch (error) {
      console.error('승인 처리 오류:', error);
      alert('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // 거부 모달 열기
  const handleShowRejectModal = (reservation) => {
    setSelectedReservation(reservation);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  // 거부 처리
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('거부 사유를 입력해주세요.');
      return;
    }

    if (!window.confirm('이 예약을 거부하시겠습니까?')) {
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/cps-settlements/reservations/${selectedReservation._id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rejectionReason })
      });
      const data = await res.json();
      
      if (data.ok) {
        alert('거부가 완료되었습니다.');
        setShowRejectModal(false);
        fetchReservations(filter);
      } else {
        alert(data.message || '거부에 실패했습니다.');
      }
    } catch (error) {
      console.error('거부 처리 오류:', error);
      alert('거부 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  // 상태 배지
  const getStatusBadge = (status) => {
    const badgeClass = `cps-settlement-reservation__item-status cps-settlement-reservation__item-status--${status}`;
    const text = {
      pending: '승인 대기',
      approved: '승인 완료',
      rejected: '거부됨'
    }[status] || '승인 대기';
    
    return (
      <span className={badgeClass}>
        {text}
      </span>
    );
  };

  return (
    <div className="cps-settlement-reservation">
      <h2 className="cps-settlement-reservation__title">구매당 리워드 결산 미진행건 처리</h2>

      {/* 필터 */}
      <div className="cps-settlement-reservation__filters">
        <button
          onClick={() => setFilter('pending')}
          className={`cps-settlement-reservation__filter-button cps-settlement-reservation__filter-button--pending ${
            filter === 'pending' ? 'cps-settlement-reservation__filter-button--active' : ''
          }`}
        >
          승인 대기 중
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`cps-settlement-reservation__filter-button cps-settlement-reservation__filter-button--approved ${
            filter === 'approved' ? 'cps-settlement-reservation__filter-button--active' : ''
          }`}
        >
          승인 완료
        </button>
        <button
          onClick={() => setFilter('rejected')}
          className={`cps-settlement-reservation__filter-button cps-settlement-reservation__filter-button--rejected ${
            filter === 'rejected' ? 'cps-settlement-reservation__filter-button--active' : ''
          }`}
        >
          거부됨
        </button>
      </div>

      {/* 로딩 */}
      {loading && <div className="cps-settlement-reservation__loading">로딩 중...</div>}

      {/* 목록 */}
      {!loading && reservations.length === 0 && (
        <div className="cps-settlement-reservation__empty">
          예약 내역이 없습니다.
        </div>
      )}

      {!loading && reservations.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>NO</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>프로젝트 번호</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>광고주</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>회차</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>인원</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>총 지급 포인트</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>자동 예약일</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>상태</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>작업</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((reservation, idx) => (
                <tr key={reservation._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600 }}>
                    {reservation.projectNumber}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    {reservation.marketerName}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    {reservation.roundNumber}회차
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    {reservation.influencerCount}명
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center', color: '#10b981', fontWeight: 700 }}>
                    {reservation.totalRewardAmount.toLocaleString()}pt
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: 12 }}>
                    {formatDate(reservation.autoReservedAt)}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    {getStatusBadge(reservation.status)}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleShowDetail(reservation)}
                      style={{
                        padding: '6px 12px',
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: 'pointer',
                        marginRight: 4
                      }}
                    >
                      상세
                    </button>
                    {reservation.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(reservation._id)}
                          disabled={processing}
                          style={{
                            padding: '6px 12px',
                            background: processing ? '#d1d5db' : '#10b981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            fontSize: 12,
                            cursor: processing ? 'not-allowed' : 'pointer',
                            marginRight: 4
                          }}
                        >
                          승인
                        </button>
                        <button
                          onClick={() => handleShowRejectModal(reservation)}
                          disabled={processing}
                          style={{
                            padding: '6px 12px',
                            background: processing ? '#d1d5db' : '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            fontSize: 12,
                            cursor: processing ? 'not-allowed' : 'pointer'
                          }}
                        >
                          거부
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 상세 모달 */}
      {showDetailModal && selectedReservation && (
        <div style={styles.overlay} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowDetailModal(false)} style={styles.closeBtn}>×</button>
            
            <h3 style={{ marginBottom: 16 }}>예약 상세 정보</h3>
            
            <div style={{ marginBottom: 12 }}>
              <strong>프로젝트:</strong> {selectedReservation.projectNumber}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>광고주:</strong> {selectedReservation.marketerName}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>회차:</strong> {selectedReservation.roundNumber}회차
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>원래 결산 예정일:</strong> {formatDate(selectedReservation.originalScheduledDate)}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>자동 예약 생성일:</strong> {formatDate(selectedReservation.autoReservedAt)}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>상태:</strong> {getStatusBadge(selectedReservation.status)}
            </div>
            
            {selectedReservation.status === 'approved' && selectedReservation.processedAt && (
              <div style={{ marginBottom: 12 }}>
                <strong>처리일:</strong> {formatDate(selectedReservation.processedAt)}
              </div>
            )}
            
            {selectedReservation.status === 'rejected' && (
              <div style={{ marginBottom: 12 }}>
                <strong>거부 사유:</strong> {selectedReservation.rejectionReason || '-'}
              </div>
            )}
            
            <h4 style={{ marginTop: 24, marginBottom: 12 }}>인플루언서별 지급 내역</h4>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '8px', textAlign: 'center' }}>NO</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>이름</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>아이디</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>지급 포인트</th>
                </tr>
              </thead>
              <tbody>
                {selectedReservation.paymentData.map((payment, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{idx + 1}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{payment.influencerName}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
                      {payment.influencerUserId}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center', color: '#10b981', fontWeight: 600 }}>
                      {payment.rewardAmount.toLocaleString()}pt
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', borderRadius: 8, textAlign: 'right' }}>
              <strong>총 지급 포인트:</strong>{' '}
              <span style={{ fontSize: 18, color: '#10b981', fontWeight: 700 }}>
                {selectedReservation.totalRewardAmount.toLocaleString()}pt
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 거부 모달 */}
      {showRejectModal && selectedReservation && (
        <div style={styles.overlay} onClick={() => !processing && setShowRejectModal(false)}>
          <div style={{ ...styles.modal, maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => !processing && setShowRejectModal(false)} style={styles.closeBtn}>×</button>
            
            <h3 style={{ marginBottom: 16 }}>예약 거부</h3>
            
            <div style={{ marginBottom: 12 }}>
              <strong>프로젝트:</strong> {selectedReservation.projectNumber}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>회차:</strong> {selectedReservation.roundNumber}회차
            </div>
            
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
              거부 사유 *
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="거부 사유를 입력해주세요..."
              maxLength={2000}
              disabled={processing}
              style={{
                width: '100%',
                minHeight: 100,
                padding: 12,
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              {rejectionReason.length} / 2000자
            </div>
            
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
                style={{
                  padding: '10px 20px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                취소
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                style={{
                  padding: '10px 20px',
                  background: (processing || !rejectionReason.trim()) ? '#d1d5db' : '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: (processing || !rejectionReason.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {processing ? '처리 중...' : '거부하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    padding: 32,
    width: '90vw',
    maxWidth: 800,
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  },
  closeBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    background: 'none',
    border: 'none',
    fontSize: 32,
    cursor: 'pointer',
    color: '#6b7280',
    lineHeight: 1,
    padding: 4
  }
};

